import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, ButtonGroup, Card, Col, Row, Form, Table, Dropdown, Modal } from 'react-bootstrap';
import { Radar } from 'react-chartjs-2';
import 'chart.js/auto';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import { getUserId } from '../services/authorize';
import { useLoading } from '../contexts/LoadingContext';
import { useIsMobile } from './guidanceShared';

const API = process.env.REACT_APP_API;
const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api')
  .replace(/\/api\/?$/, '').replace(/\/$/, '');

// 8 กลุ่มสาระ — เรียงตาม backend (talentController.CATEGORIES)
const CATEGORIES = [
  { key: 'thai',    label: 'ภาษาไทย',       short: 'ไทย' },
  { key: 'english', label: 'ภาษาอังกฤษ',    short: 'อังกฤษ' },
  { key: 'math',    label: 'คณิตศาสตร์',     short: 'คณิต' },
  { key: 'science', label: 'วิทยาศาสตร์',    short: 'วิทย์' },
  { key: 'social',  label: 'สังคมศึกษา',     short: 'สังคม' },
  { key: 'art',     label: 'ศิลปะ',          short: 'ศิลป์' },
  { key: 'pe',      label: 'สุขศึกษา/พละ',   short: 'พละ' },
  { key: 'career',  label: 'การงานอาชีพ',    short: 'การงาน' },
];
const RADAR_LABELS = CATEGORIES.map((c) => c.short);

const STUDENT_COLOR = '#506fd9';
const ROOM_COLOR = '#e5a11f';

const scoreTextClass = (v) => {
  if (v == null) return 'text-muted';
  if (v >= 80) return 'text-success fw-semibold';
  if (v >= 70) return 'text-primary';
  if (v >= 50) return 'text-warning';
  return 'text-danger';
};
const fmt = (v) => (v == null ? '-' : Number(v).toFixed(1));

const scoresOf = (talent) =>
  CATEGORIES.map((c) => (talent && talent[c.key] ? talent[c.key].score : null));

// c_class เป็นข้อความเต็ม ("ชั้นมัธยมศึกษาปีที่ 3") — ระดับชั้นคือ c_id (1–6)
const roomLabelOf = (r) => (r ? `ม.${r.c_id}/${r.r_no}` : '');

// ค่าเฉลี่ยต่อหมวด (เฉลี่ยเฉพาะนักเรียนที่มีคะแนนหมวดนั้น)
const averagesOf = (list) =>
  CATEGORIES.map((c) => {
    const vals = list
      .map((s) => (s.talent && s.talent[c.key] ? s.talent[c.key].score : null))
      .filter((v) => v != null);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  });

// หมวดเด่นสุด/อ่อนสุดจากชุดค่าเฉลี่ย
const topLowOf = (averages) => {
  let topCat = null, lowCat = null;
  averages.forEach((v, i) => {
    if (v == null) return;
    if (topCat === null || v > averages[topCat]) topCat = i;
    if (lowCat === null || v < averages[lowCat]) lowCat = i;
  });
  return { topCat, lowCat: lowCat === topCat ? null : lowCat };
};

const radarOptions = {
  maintainAspectRatio: false,
  responsive: true,
  scales: {
    r: {
      min: 0,
      max: 100,
      ticks: { stepSize: 20, backdropColor: 'transparent', font: { size: 10 } },
      pointLabels: { font: { size: 12 } },
    },
  },
  plugins: {
    legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 12 } } },
    tooltip: {
      callbacks: {
        label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.r == null ? '-' : ctx.parsed.r.toFixed(1)}`,
      },
    },
  },
};

const StudentAvatar = ({ photo, name, size = 32 }) => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initial = name ? name.trim().charAt(0) : '?';
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null;
  const circleStyle = { width: size, height: size, borderRadius: '50%', flexShrink: 0 };

  if (!src) {
    return (
      <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 600 }}>{initial}</span>
    );
  }

  return (
    <>
      <span
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={(e) => { e.stopPropagation(); setShowModal(true); }}
      >
        <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover', cursor: 'pointer' }} />
        {hovered && (
          <div style={{ position: 'absolute', left: size + 6, top: '50%', transform: 'translateY(-50%)', zIndex: 1050, background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
          </div>
        )}
      </span>
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
        <Modal.Body className="p-3 text-center">
          <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
          {name && <div className="mt-2 fw-semibold small">{name}</div>}
        </Modal.Body>
      </Modal>
    </>
  );
};

// มุมมองการ์ดของแถวในตารางคะแนนรายกลุ่มสาระ (อ่านง่ายกว่าตารางบนจอแคบ)
const TalentStudentCard = ({ student, index, onClick }) => {
  const scores = scoresOf(student.talent);
  return (
    <div className="border rounded p-3 h-100" style={{ cursor: 'pointer' }} onClick={onClick}>
      <div className="d-flex align-items-center gap-2">
        <StudentAvatar photo={student.st_photo} name={student.st_name} size={40} />
        <div style={{ minWidth: 0 }}>
          <div className="fw-semibold text-truncate">{student.st_name}</div>
          <div className="fs-xs text-muted">
            เลขที่ {index + 1}{student.st_idnumber ? ` · ${student.st_idnumber}` : ''}
          </div>
        </div>
      </div>
      <Row className="g-1 text-center border-top mt-2 pt-2">
        {CATEGORIES.map((c, i) => (
          <Col xs={3} key={c.key}>
            <div className="fs-xs text-muted text-truncate">{c.short}</div>
            <div className={`fs-sm ${scoreTextClass(scores[i])}`}>{fmt(scores[i])}</div>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default function AdvisorTalent() {
  const { setLoading } = useLoading();
  const [skin, setSkin] = useState(localStorage.getItem('skin-mode') ? 'dark' : '');

  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState(null);
  const effectiveView = viewMode ?? (isMobile ? 'card' : 'table');

  const [yearList, setYearList] = useState([]);
  const [yearId, setYearId] = useState(() => localStorage.getItem('yearId') || null);
  const [rooms, setRooms] = useState([]);
  const [roomsLoaded, setRoomsLoaded] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState('');
  const [room, setRoom] = useState(null);
  const [students, setStudents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [detail, setDetail] = useState(null); // นักเรียนที่เลือกดู radar

  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`)
      .then((res) => {
        const list = res.data || [];
        setYearList(list);
        if (list.length > 0) setYearId((prev) => prev || String(list[0].year_id));
      })
      .catch(() => {});
  }, []);

  // ห้องที่ครูคนนี้เป็นที่ปรึกษาในปีที่เลือกเท่านั้น
  useEffect(() => {
    if (!yearId) return;
    setRoomsLoaded(false);
    setSelectedRoom('');
    axiosInstance.get(`${API}/getAdvisorRooms/${getUserId()}/${yearId}`)
      .then((res) => {
        const list = res.data || [];
        setRooms(list);
        if (list.length > 0) setSelectedRoom(String(list[0].r_id));
      })
      .catch(() => setRooms([]))
      .finally(() => setRoomsLoaded(true));
  }, [yearId]);

  useEffect(() => {
    if (!selectedRoom) { setStudents([]); setRoom(null); setLoaded(false); return; }
    setLoading(true);
    setLoaded(false);
    axiosInstance.get(`${API}/talent/academic/room/${selectedRoom}`)
      .then((res) => {
        setRoom(res.data?.room || null);
        setStudents(res.data?.students || []);
      })
      .catch(() => { setRoom(null); setStudents([]); })
      .finally(() => { setLoaded(true); setLoading(false); });
  }, [setLoading, selectedRoom]);

  const roomAverages = useMemo(() => averagesOf(students), [students]);
  const { topCat, lowCat } = useMemo(() => topLowOf(roomAverages), [roomAverages]);

  const roomRadarData = useMemo(() => ({
    labels: RADAR_LABELS,
    datasets: [{
      label: 'ค่าเฉลี่ยห้อง',
      data: roomAverages.map((v) => (v == null ? 0 : Number(v.toFixed(1)))),
      backgroundColor: 'rgba(229,161,31,0.18)',
      borderColor: ROOM_COLOR,
      pointBackgroundColor: ROOM_COLOR,
      borderWidth: 2,
    }],
  }), [roomAverages]);

  const detailRadarData = useMemo(() => {
    if (!detail) return null;
    return {
      labels: RADAR_LABELS,
      datasets: [
        {
          label: detail.st_name,
          data: scoresOf(detail.talent).map((v) => (v == null ? 0 : Number(v.toFixed(1)))),
          backgroundColor: 'rgba(80,111,217,0.2)',
          borderColor: STUDENT_COLOR,
          pointBackgroundColor: STUDENT_COLOR,
          borderWidth: 2,
        },
        {
          label: 'ค่าเฉลี่ยห้อง',
          data: roomAverages.map((v) => (v == null ? 0 : Number(v.toFixed(1)))),
          backgroundColor: 'rgba(229,161,31,0.10)',
          borderColor: ROOM_COLOR,
          pointBackgroundColor: ROOM_COLOR,
          borderWidth: 1.5,
          borderDash: [5, 4],
        },
      ],
    };
  }, [detail, roomAverages]);

  const selectedYearLabel = useMemo(() => {
    const y = yearList.find((item) => String(item.year_id) === String(yearId));
    return y ? `ปีการศึกษา ${y.year_year}` : 'เลือกปีการศึกษา';
  }, [yearList, yearId]);

  const roomLabel = roomLabelOf(room);
  const hasAnyData = students.some((s) => scoresOf(s.talent).some((v) => v != null));

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/advisor/dashboard">ที่ปรึกษาห้องเรียน</Link></li>
              <li className="breadcrumb-item active" aria-current="page">จุดเด่นทางวิชาการ</li>
            </ol>
            <h4 className="main-title mb-0">
              จุดเด่นทางวิชาการ (Talent)
              {roomLabel && <span className="badge bg-primary ms-2 fs-14">{roomLabel}</span>}
            </h4>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="dropdown-year">{selectedYearLabel}</Dropdown.Toggle>
              <Dropdown.Menu>
                {yearList.map((item) => (
                  <Dropdown.Item
                    key={item.year_id}
                    active={String(item.year_id) === String(yearId)}
                    onClick={() => {
                      setYearId(String(item.year_id));
                      localStorage.setItem('yearId', item.year_id);
                    }}
                  >
                    {`ปีการศึกษา ${item.year_year}`}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <Card className="card-one mb-3">
          <Card.Body>
            <Row className="g-2 align-items-end">
              {/* เลือกห้องได้เฉพาะเมื่อเป็นที่ปรึกษามากกว่า 1 ห้องในปีนั้น */}
              {rooms.length > 1 && (
                <Col md={5}>
                  <Form.Label className="fs-sm mb-1">ห้องเรียนที่ปรึกษา</Form.Label>
                  <Form.Select value={selectedRoom} onChange={(e) => setSelectedRoom(e.target.value)}>
                    {rooms.map((r) => (
                      <option key={r.r_id} value={r.r_id}>{roomLabelOf(r)}</option>
                    ))}
                  </Form.Select>
                </Col>
              )}
              <Col md={rooms.length > 1 ? 7 : 12}>
                <div className="fs-xs text-muted">
                  <i className="ri-information-line me-1" />
                  คะแนนเป็นค่าเฉลี่ย (0–100) จากผลการเรียนสะสมทุกภาคเรียนของนักเรียนแต่ละคน แยกตามกลุ่มสาระ
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {roomsLoaded && rooms.length === 0 && (
          <Card className="card-one mb-4">
            <Card.Body className="text-center py-5 text-muted">
              <i className="ri-information-line fs-3 d-block mb-2" />
              ยังไม่ได้รับมอบหมายเป็นครูประจำชั้นในปีการศึกษานี้
            </Card.Body>
          </Card>
        )}

        {selectedRoom && loaded && !hasAnyData && (
          <Card className="card-one mb-4">
            <Card.Body className="text-center py-5 text-muted">
              <i className="ri-bar-chart-box-line fs-3 d-block mb-2" />
              ยังไม่มีข้อมูลผลการเรียนของห้องนี้
            </Card.Body>
          </Card>
        )}

        {selectedRoom && hasAnyData && (
          <Row className="g-3 mb-4">
            <Col xl={5}>
              <Card className="card-one h-100">
                <Card.Header>
                  <Card.Title as="h6" className="mb-0">ภาพรวมห้อง {roomLabel}</Card.Title>
                </Card.Header>
                <Card.Body>
                  <div style={{ height: 320 }}>
                    <Radar data={roomRadarData} options={radarOptions} />
                  </div>
                  <div className="d-flex justify-content-around mt-3 pt-3 border-top text-center">
                    <div>
                      <div className="fs-xs text-muted">กลุ่มสาระเด่นสุด</div>
                      <div className="fw-semibold text-success">
                        {topCat != null ? `${CATEGORIES[topCat].label} (${fmt(roomAverages[topCat])})` : '-'}
                      </div>
                    </div>
                    <div>
                      <div className="fs-xs text-muted">ควรพัฒนา</div>
                      <div className="fw-semibold text-danger">
                        {lowCat != null ? `${CATEGORIES[lowCat].label} (${fmt(roomAverages[lowCat])})` : '-'}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={7}>
              <Card className="card-one h-100">
                <Card.Header className="d-flex align-items-center">
                  <Card.Title as="h6" className="mb-0">
                    คะแนนรายกลุ่มสาระ
                    <Badge bg="secondary" className="ms-2">{students.length} คน</Badge>
                  </Card.Title>
                  <div className="ms-auto d-flex align-items-center gap-2">
                    <span className="fs-xs text-muted d-none d-xl-inline">คลิกเพื่อดูกราฟเรดาร์รายคน</span>
                    {/* ปุ่มสลับมุมมอง การ์ด / ตาราง */}
                    <ButtonGroup size="sm">
                      <Button
                        variant={effectiveView === 'card' ? 'primary' : 'outline-primary'}
                        onClick={() => setViewMode('card')}
                        title="มุมมองการ์ด"
                      >
                        <i className="ri-layout-grid-line"></i>
                      </Button>
                      <Button
                        variant={effectiveView === 'table' ? 'primary' : 'outline-primary'}
                        onClick={() => setViewMode('table')}
                        title="มุมมองตาราง"
                      >
                        <i className="ri-table-line"></i>
                      </Button>
                    </ButtonGroup>
                  </div>
                </Card.Header>
                <Card.Body className={effectiveView === 'table' ? 'p-0' : 'p-3'}>
                  {effectiveView === 'table' ? (
                    <div style={{ overflowX: 'auto' }}>
                      <Table hover className="mb-0 align-middle" style={{ minWidth: 720 }}>
                        <thead className="table-secondary">
                          <tr>
                            <th className="text-center" style={{ width: 44 }}>#</th>
                            <th style={{ minWidth: 150 }}>ชื่อ-สกุล</th>
                            {CATEGORIES.map((c) => (
                              <th key={c.key} className="text-center" style={{ width: 62 }}>{c.short}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {students.map((s, index) => {
                            const scores = scoresOf(s.talent);
                            return (
                              <tr key={s.st_id} style={{ cursor: 'pointer' }} onClick={() => setDetail(s)}>
                                <td className="text-center text-muted">{index + 1}</td>
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <StudentAvatar photo={s.st_photo} name={s.st_name} size={28} />
                                    <span className="text-truncate" style={{ maxWidth: 180 }}>{s.st_name}</span>
                                  </div>
                                </td>
                                {scores.map((v, i) => (
                                  <td key={i} className={`text-center ${scoreTextClass(v)}`}>{fmt(v)}</td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <Row className="g-3">
                      {students.map((s, index) => (
                        <Col xs={12} md={6} key={s.st_id}>
                          <TalentStudentCard student={s} index={index} onClick={() => setDetail(s)} />
                        </Col>
                      ))}
                    </Row>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        <Modal show={!!detail} onHide={() => setDetail(null)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title as="h6">
              <StudentAvatar photo={detail?.st_photo} name={detail?.st_name} size={32} />
              <span className="ms-2">{detail?.st_name}</span>
              {detail?.st_idnumber && <span className="text-muted ms-2 fs-sm">({detail.st_idnumber})</span>}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {detail && (
              <Row className="g-3 align-items-center">
                <Col md={7}>
                  <div style={{ height: 340 }}>
                    {detailRadarData && <Radar data={detailRadarData} options={radarOptions} />}
                  </div>
                </Col>
                <Col md={5}>
                  <Table size="sm" className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>กลุ่มสาระ</th>
                        <th className="text-center">คะแนน</th>
                        <th className="text-center">วิชา</th>
                      </tr>
                    </thead>
                    <tbody>
                      {CATEGORIES.map((c) => {
                        const cell = detail.talent && detail.talent[c.key];
                        return (
                          <tr key={c.key}>
                            <td>{c.label}</td>
                            <td className={`text-center ${scoreTextClass(cell ? cell.score : null)}`}>
                              {fmt(cell ? cell.score : null)}
                            </td>
                            <td className="text-center text-muted">{cell ? cell.count : '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            )}
          </Modal.Body>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}
